import { useEffect, useState } from "react";
import { PackageSearch, ShoppingBag } from "lucide-react";
import { FlameMark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatINR } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  PLACED: "border-ember/50 text-ember",
  CONFIRMED: "border-sky-400/50 text-sky-300",
  DISPATCHED: "border-purple-400/50 text-purple-300",
  DELIVERED: "border-discount-green/50 text-discount-green",
  CANCELLED: "border-crimson/50 text-crimson",
};

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      window.location.href = "/login";
      return;
    }
    fetch(`${API}/auth/my-orders`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setOrders)
      .catch(() => setOrders([]));
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-obsidian text-starlight flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-night">Opening your account…</p>
      </div>
    );
  }
  if (user === false) return null;

  return (
    <div className="min-h-screen bg-obsidian text-starlight" data-testid="account-page">
      <header className="border-b border-starlight/10 bg-card/40">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <FlameMark className="h-8 w-8" />
            <span className="leading-none">
              <span className="block font-display font-bold tracking-[0.18em] text-starlight text-lg">UTSAV</span>
              <span className="block font-mono text-[10px] tracking-[0.42em] text-ember mt-0.5">MY ACCOUNT</span>
            </span>
          </a>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-ember/30" />}
            <button
              data-testid="account-logout-button"
              onClick={async () => {
                await logout();
                window.location.href = "/";
              }}
              className="rounded-full border border-crimson/50 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-crimson hover:bg-crimson hover:text-starlight transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-8 py-12">
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-ember">Signed in as</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-starlight">{user.name}</h1>
        <p className="text-sm text-muted-night">{user.email}</p>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-starlight">Your orders</h2>
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-full border border-starlight/15 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
          >
            <ShoppingBag className="h-3.5 w-3.5" /> Shop more
          </a>
        </div>

        {orders === null && <p className="mt-8 font-mono text-xs text-muted-night">Loading your orders…</p>}

        {orders && orders.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-starlight/15 py-16 text-center">
            <PackageSearch className="mx-auto h-8 w-8 text-muted-night/50" />
            <p className="mt-4 font-display text-xl text-starlight">No orders yet</p>
            <p className="mt-2 text-sm text-muted-night">Pick some crackers and your orders will appear here.</p>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {orders?.map((o) => (
            <a
              key={o.order_number}
              href={`/track?uc=${o.order_number}`}
              data-testid={`account-order-${o.order_number}`}
              className="block rounded-2xl border border-starlight/10 bg-card p-5 transition-colors hover:border-ember/50"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-lg font-bold text-ember">{o.order_number}</p>
                  <p className="font-mono text-[10px] text-muted-night mt-0.5">
                    {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full border px-3 py-1 font-mono text-[10px] font-bold tracking-[0.14em] ${STATUS_STYLE[o.status] || ""}`}>
                    {o.status}
                  </span>
                  <p className="font-mono text-base font-bold text-starlight">{formatINR(o.total)}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-night">
                {o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}
              </p>
            </a>
          ))}
        </div>
      </main>
    </div>
  );
}
